import { useEffect, useState } from 'react';
import { probeFirebaseReachability } from '../utils/networkProbe';
import { safeStorage } from '../utils/storage';

const DISMISS_KEY = 'adBlockerWarningDismissed';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Top-of-page banner that appears when we detect Firebase is being
 * blocked (ad-blocker, tracking protection, firewall).
 *
 * Why this matters: writes still LOOK fine locally because IndexedDB
 * persistence holds them, but they may never reach the server. The user
 * sees no error, just data that mysteriously doesn't sync across devices.
 *
 * Dismissable for a week per user.
 */
export default function AdBlockerBanner() {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    // Skip if user dismissed recently
    const dismissedAt = parseInt(safeStorage.get(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) {
      setStatus('dismissed');
      return;
    }

    // Defer to idle so we don't compete with the first paint
    const run = async () => {
      const result = await probeFirebaseReachability();
      setStatus(result);
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(run, 1500);
    return () => clearTimeout(t);
  }, []);

  if (status !== 'blocked') return null;

  const handleDismiss = () => {
    safeStorage.set(DISMISS_KEY, String(Date.now()));
    setStatus('dismissed');
  };

  return (
    <div className="adblock-banner" role="alert">
      <div className="adblock-banner-icon">⚠️</div>
      <div className="adblock-banner-body">
        <strong>Tu navegador puede estar bloqueando la sincronización con la nube.</strong>
        <span>
          Detectamos que <code>firestore.googleapis.com</code> está bloqueado.
          Tus cambios se guardan localmente pero podrían no sincronizarse con otros dispositivos.
        </span>
        <span className="adblock-banner-help">
          Si usas un ad-blocker o protección anti-tracking, agrega <strong>{location.host}</strong> a la whitelist o desactiva la protección para este sitio.
        </span>
      </div>
      <button className="adblock-banner-dismiss" onClick={handleDismiss} aria-label="Cerrar aviso">
        ✕
      </button>
    </div>
  );
}
